import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API } from "@/lib/scroll";

const SERVICES = ["Branding & Design", "Digital Ads & SEO", "Google Ads", "Meta Ads", "Yelp Ads", "Web Development", "Social Media & Content", "Full Growth Engagement"];
const BUDGETS = ["Under $10k", "$10k – $25k", "$25k – $50k", "$50k+"];

const inputCls =
  "w-full bg-[#0A1128]/80 border border-amber-500/20 px-4 py-3.5 text-sm text-amber-50 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 transition-colors duration-300";

const INITIAL = { name: "", email: "", phone: "", company: "", service: SERVICES[0], budget: BUDGETS[1], message: "" };

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. A partner will reach out within one business day.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(err.response?.data?.detail ? "Please check the form and try again." : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#060B18] py-28 lg:py-36 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute -bottom-40 left-1/2 -translate-x-1/2 h-[420px] w-[720px] rounded-full bg-amber-400/5 blur-3xl"
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400/90 mb-4">Start the Conversation</p>
          <h2 className="font-serif font-bold tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl text-amber-50">
            Build it
            <br />
            <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11]">
              right.
            </span>
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-slate-300">
            Tell us where you are and where you're headed. A senior partner — never a bot, never a junior —
            replies within one business day.
          </p>
          <div className="mt-12 space-y-5">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500/70">Locations</p>
              <p className="mt-1 text-sm text-slate-300">Serving businesses all over California</p>
              <p className="mt-1 text-sm text-slate-400">San Francisco · Silicon Valley · Los Angeles · San Diego · Sacramento</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500/70">Direct</p>
              <p className="mt-1 text-sm text-slate-300">
                <a href="mailto:sales@buildrightpartners.net" className="hover:text-amber-300 transition-colors" data-testid="contact-email-link">sales@buildrightpartners.net</a>
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500/70">Call Us</p>
              <p className="mt-1 text-sm text-slate-300">
                <a href="tel:+18339957227" data-testid="contact-phone-tollfree" className="hover:text-amber-300 transition-colors">1-833-995-7227</a>
                <span className="text-slate-600"> (toll-free)</span>
              </p>
              <p className="mt-1 text-sm text-slate-300">
                <a href="tel:+16197347299" data-testid="contact-phone-local" className="hover:text-amber-300 transition-colors">619-734-7299</a>
                <span className="text-slate-600"> (direct)</span>
              </p>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="bg-[#0A1128]/70 backdrop-blur-xl border border-amber-500/20 p-8 lg:p-10 space-y-5 shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              data-testid="contact-form-name-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Full name *"
              className={inputCls}
            />
            <input
              data-testid="contact-form-email-input"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="Work email *"
              className={inputCls}
            />
            <input
              data-testid="contact-form-phone-input"
              value={form.phone}
              onChange={set("phone")}
              placeholder="Phone"
              className={inputCls}
            />
            <input
              data-testid="contact-form-company-input"
              value={form.company}
              onChange={set("company")}
              placeholder="Company"
              className={inputCls}
            />
            <select
              data-testid="contact-form-service-select"
              value={form.service}
              onChange={set("service")}
              className={`${inputCls} appearance-none`}
            >
              {SERVICES.map((s) => (
                <option key={s} value={s} className="bg-[#0A1128]">
                  {s}
                </option>
              ))}
            </select>
            <select
              data-testid="contact-form-budget-select"
              value={form.budget}
              onChange={set("budget")}
              className={`${inputCls} appearance-none`}
            >
              {BUDGETS.map((b) => (
                <option key={b} value={b} className="bg-[#0A1128]">
                  {b}
                </option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="contact-form-message-textarea"
            required
            rows={5}
            value={form.message}
            onChange={set("message")}
            placeholder="What are you building, and what does growth look like for you? *"
            className={`${inputCls} resize-none`}
          />
          <button
            data-testid="contact-form-submit-button"
            type="submit"
            disabled={sending}
            className="w-full rounded-full bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] py-4 font-mono text-xs uppercase tracking-[0.25em] font-semibold text-[#060B18] hover:shadow-[0_0_35px_rgba(212,175,55,0.35)] transition-shadow duration-500 disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send Enquiry"}
          </button>
          <p className="text-center font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">
            Confidential. No spam. No obligation.
          </p>
        </motion.form>
      </div>
    </section>
  );
};
